<template>
<div v-if="deleteMode" class="modal-card">
  <header class="modal-card-head">
    <p class="modal-card-title">Undo All Volumes</p>
  </header>
  <section class="modal-card-body">
    <div class="content" v-if="state=='init'">
      <p>Use the "Undo Volumes" option below to revert the previous
      splitting up into volumes. You might want to do this if you have
      changed blog content or the page count has otherwise changed.</p>

      <p>All volume-specific customisations to the covers will be
        lost. After this, you will be able to recreate the volumes in
        the same way.</p>
    </div>
    <div class="content" v-else-if="state=='success'">
      <h2>Success!</h2>
      <p>We will now return to your preview where you may recreate your
      volumes.</p>
    </div>
    <div class="content" v-else-if="state=='error'">
      <h2>Oh No! {{error}}</h2>
      <p>Please contact us so we can fix it!</p>
    </div>
  </section>  
  <footer class="modal-card-foot">
    <div class="level">
      <div class="level-left"> </div>
      <div class="level-right">
        <button v-if="state!='success'"class="button" type="button" @click="doClose">Cancel</button>
        <button v-else class="button" type="button" @click="doClose">Done</button>
        <button v-if="state=='init'" class="button is-primary"
                @click="doDelete">Undo {{numVolumes}} Volumes</button>
      </div>
    </div>
  </footer>
</div>

<div v-else class="modal-card">  
    <header class="modal-card-head">
      <p class="modal-card-title">Splitting into Volumes</p>
    </header>
    
    <section class="modal-card-body" v-if="state=='init'">
      <div class="content">
        <div>
          <b-message v-if="needsMoreProcessing" type="is-warning"
                     title="We can't split this just yet"> Please wait
            for more of the preview to be built so that we can be sure
            to split it acurately... (waiting for
            {{numberOfPostsWaiting}} more)
          </b-message>

          <b-field label="Scheme:"></b-field>
          <table>
            <tr v-for="vol,idx in volumeDistribution">
              <td>Volume&nbsp;{{idx+1}}:</td>
              <td>from: <span class="post-line">{{postDate(firstPost(vol)) | formatDate}}
                  &mdash; {{postTitle(firstPost(vol))}}</span><br>
                to: <span class="post-line">{{postDate(lastPost(vol)) | formatDate}}
                  &mdash; {{postTitle(lastPost(vol))}}</span><br>
                <span>{{vol.posts}} posts, {{vol.pages}} pages</span>
            </td></tr>
          </table>

          <b-field label="Add Volume Label to Cover:">
            <b-select v-model="labelPlacement" expanded>
              <option value="title">Add the volume number to the cover Title</option>
              <option value="author">Replace cover Author with volume number</option>
              <option value="subtitle">Replace cover SubTitle with
              volume number (this won't show on the spine)</option>
              <option value="none">No volume label</option>
            </b-select>
          </b-field>

            <p><i>Note:  We recommend that you split into volumes only after you have
              reviewed and are happy with the content. If you make further
              tweaks to the content, it may change the page counts and you
              may need to re-split the volumes. </i></p>
        </div>
      </div>
    </section>

    <section class="modal-card-body" v-else-if="state=='busy'">
      <div class="content" id="busy">
        Just a moment...
      </div>
    </section>

    <section class="modal-card-body" v-else-if="state=='success'">
      <div class="content">
        <h2>Success!</h2>
        <p>We will now return to your preview.</p>
      </div>
    </section>

    <section class="modal-card-body" v-else>
      <div class="content">
        <h2>Oh No! {{error}}</h2>
        <p>Please contact us so we can fix it!</p>
      </div>
    </section>
    
    <footer class="modal-card-foot">
      <div class="level">
        <div class="level-left"> </div>
        <div class="level-right">
          <button v-if="state!='success'"class="button" type="button" @click="doClose">Cancel</button>
          <button v-if="state=='success'"class="button" type="button" @click="doClose">Done</button>
          <button v-if="state=='init'" class="button is-primary"
                  :disabled="needsMoreProcessing > 0 || volumeDistribution.length < 2"
                  @click="doSplit">Split into {{volumeDistribution.length}} Volumes</button>
        </div>
      </div>
    </footer>
</div>
</template>


<script>
import Firebase from '../firebase.js'
import { CalcVolumeDistribution } from './voluminator_utils.js'
var db = Firebase.database

function dupeVolumeFrom(info) {
  const FIELDS = [ 'coverTitle',
                   'coverAuthor',
                   'coverDescription',
                   'coverCustomImage',
                   'coverBlurb1',
                   'coverBlurb2',
                   'coverBlurb3',
                   'coverBgColor',
                   'coverTextColor',
                   'coverShowTitlePage',
                   'dateFrom',
                   'dateTo',
                   'selectedPosts',
                   'volumeLabelPosition']
  return Object.fromEntries(FIELDS
                            .filter(f => info[f]!==undefined)
                            .map(f => [f,info[f]]))
}

export default {
  name: 'Voluminator',
  data: function() { return {
    project: {},
    state: 'init',
    deleteMode: false,
    chosenLabelPlacement: false,
  }},
  props: {
    projid: { type: String, required: true },
  },
  computed: {
    uid: function() {
      return this.$store.state.uid
    },
    numVolumes: function() {
      if (this.project
          && this.project.selections
          && this.project.selections.volumes) {
        return Object.keys(this.project.selections.volumes).length
      }
      return 0
    },
    selections: function() {
      if (this.project) {
        return this.project.selections || {}
      }
      return {}
    },
    labelPlacement: {
      get: function() {
        return this.chosenLabelPlacement
          || this.selections.volumeLabel
          || (this.selections.coverAuthor ? undefined : 'author')
          || (this.selections.coverTitle && this.selections.coverTitle.length < 40 ? 'title' : 'none')
      },
      set: function(val) {
        this.chosenLabelPlacement = val
      }
    },
    currentVolume: function() {
      if (this.selections.volumes) {
        return Object.values(this.selections.volumes)
          .reduce((a,b) => a.volumeNumber > b.volumeNumber ? a : b)
      }
      return this.selections
    },
    pageEstimates: function() {
      return this.stats.pageestimate || {}
    },
    pageCounts: function() {
      return this.stats.pagecounts || {}
    },
    needsMoreProcessing: function() {
      return this.volumeDistribution.map(v=>v.busy).reduce((a,b)=>a+b,0)
    },
    numberOfPostsWaiting: function() {
      return parseInt(this.needsMoreProcessing/1000+(this.needsMoreProcessing%1000))
    },
    volumeDistribution: function() {
      return CalcVolumeDistribution(this.totalPages, this.maxContentPages,
                                    this.selections.selectedPosts,
                                    this.pageCounts, this.pageEstimates)
    },
    postPages: function() {
      return this.selections.selectedPosts.map( p => this.pageCounts[p] || 0)
    },
    totalPages: function() {
      var countedPosts = this.postPages.filter(p => p >= 0).length
      var countedPages = this.postPages.reduce( (a,b) => a+b, 0)
      var estimatedPosts = this.postPages.length - countedPosts
      return Math.round(countedPages + countedPages*estimatedPosts/countedPosts)
    },
    titlePages: function() {
      return 2
    },
    maxPages: function() {
      return this.project.maxPages || this.$store.state.maxPages
    },
    maxContentPages: function() {
      return this.maxPages - this.titlePages
    },
  },
  methods: {
    firstPost: function(volume) {
      return volume.pids[0]
    },
    lastPost: function(volume) {
      return volume.pids[volume.pids.length-1]
    },
    postDate: function(pid) {
      return (this.project.posts[pid] || {date:0}).date
    },
    postTitle: function(pid) {
      return (this.project.posts[pid] || {title:'ERROR'}).title
    },
    doClose: function() {
      if (this.state == 'success') {
        this.$emit('change')
      }
      this.$parent.close()
    },
    doSplit: function() {
      if (this.needsMoreProcessing) {
        return
      }

      var vols = this.volumeDistribution.map( (vol,idx) => {
        return Object.assign(dupeVolumeFrom(this.selections),
                      { createStamp: {'.sv' : 'timestamp'},
                        volumeNumber: idx+1,
                        dateFrom: this.postDate(this.firstPost(vol)),
                        dateTo: this.postDate(this.lastPost(vol))+1,
                        selectedPosts: [0],
                      })
      })
      vols.forEach( vol => {
        this.updateVolumeSelectedPosts(vol)
        this.updateVolumeLabel(vol)
      })
      var update = vols.reduce( (upd,vol) => {
        upd['vol'+vol.volumeNumber] = vol
        return upd
      }, {})

      console.log('SPLIT:', update)
      var ref = this.$firebaseRefs.project.child('selections').child('volumes')
      ref.update(update)
        .then( () => this.state = 'success' )
        .catch( e => this.state = 'error' )
    },
    updateVolumeSelectedPosts: function(volume) {
      var posts = this.project.posts
      var selpids = this.project.selections.selectedPosts
      var dateFrom = Math.min(volume.dateFrom, volume.dateTo)
      var dateTo = Math.max(volume.dateFrom, volume.dateTo)
      volume.selectedPosts = selpids.filter(p => posts[p].date >= dateFrom && posts[p].date < dateTo)
    },
    updateVolumeLabel: function(volume) {
      var label = 'Volume&nbsp;'+volume.volumeNumber      
      if (this.labelPlacement === 'title') {
        var newSuffix = ' <em><small>'+label+'</small></em>'
        var title = volume.coverTitle || ''
        volume.coverTitle = title + newSuffix
      } else if (this.labelPlacement === 'author') {
        volume.coverAuthor = label
      } else if (this.labelPlacement === 'subtitle') {
        volume.coverDescription = label
      }
    },
    doDelete: function() {
      console.log('deleting all volumes!')
      var that = this
      that.state = 'busy'
      var ref = this.$firebaseRefs.project.child('selections').child('volumes').remove()
          .then( () => this.state = 'success' )
          .catch( e => this.state = 'error' )
    },
  },
  beforeMount: function() {
    var projpath = "/projects/"+this.$store.state.uid+"/"+this.projid
    var projref = db.ref(projpath)
    this.$bindAsObject("project", projref, null, () => {
      var tags = [this.selections.format, 'draft']
      if (this.selections && this.selections.styleHash) {
        tags.push(this.selections.styleHash)
      }
      var rendertags = tags.join('_')
      var statspath = "/renders/"+this.$store.state.uid+"/"+this.projid+"/"+rendertags+"/stats"
      console.log('loading stats', statspath)
      this.$bindAsObject("stats", db.ref(statspath))
    })
  },
  mounted: function() {
    this.deleteMode = this.numVolumes
  },
}
</script>

<style scoped>
.level {
    width: 100%;
}
.button {
    min-width: 8em;
}
.post-line {
    font-style: italic;
}
.content#busy {
    min-height: 8rem;
    background: url(/static/loading_spinner.gif) no-repeat center center #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/VoluminatorPage.vue